import React from "react";
import { HelpCircle } from "lucide-react";
import { Card } from "@/components/Card/Card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CostPerAcquisitionMetrics } from "../../api/fetchROIDashboardData/types";

interface CostPerAcquisitionCardProps {
  costPerAcquisition: CostPerAcquisitionMetrics | null;
}

export const CostPerAcquisitionCard: React.FC<CostPerAcquisitionCardProps> = ({
  costPerAcquisition,
}) => {
  const currentValue = costPerAcquisition?.currentValue;
  const changeFromLastMonth = costPerAcquisition?.changeFromLastMonth;

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
      return "N/A";
    }
    return `$${value.toLocaleString()}`;
  };

  const isPositiveChange =
    changeFromLastMonth !== null &&
    changeFromLastMonth !== undefined &&
    changeFromLastMonth < 0;
  const isNegativeChange =
    changeFromLastMonth !== null &&
    changeFromLastMonth !== undefined &&
    changeFromLastMonth > 0;

  return (
    <Card className="w-full">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Cost per Acquisition
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>
                  How much you spend on marketing to acquire each new customer.
                  Lower is better. This helps you understand if your marketing
                  is cost-effective.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="text-3xl font-bold text-gray-900 dark:text-white">
          {formatCurrency(currentValue)}
        </div>

        {changeFromLastMonth !== null && changeFromLastMonth !== undefined && (
          <div
            className={`flex items-center gap-1 mt-1 text-sm ${
              isPositiveChange
                ? "text-green-600"
                : isNegativeChange
                  ? "text-red-600"
                  : "text-gray-500"
            }`}
          >
            <span>
              {isPositiveChange ? "\u2193" : isNegativeChange ? "\u2191" : ""} $
              {Math.abs(changeFromLastMonth).toLocaleString()} from last month
            </span>
          </div>
        )}
      </div>
    </Card>
  );
};
